// Referrals and recommendations as the settings page lists them.
//
// The server keeps each person's endorsements in their profile metadata: `endorsementsReceived`
// (the record of what they were given or asked for), `endorsementsGiven` (copies of what they gave
// or were asked to give) and `referralsNamingMe` (approved referrals to them). A profile whose data
// has not been moved there yet still carries the old lists in its public data; those are shown,
// read-only, until it has been moved. Once `endorsementsReceived` exists the old lists are ignored,
// whatever an old browser may have written to them since.

const profileOf = user => user?.attributes?.profile || {};
const metadataOf = user => profileOf(user).metadata || {};
const publicDataOf = user => profileOf(user).publicData || {};

// Who may see an entry. Keep this table the same as the server's copy; both are checked against
// one shared case list.
export const visibleTo = (entry, audience) => {
  if (!entry) return false;
  if (audience === 'recipient' || audience === 'giver') return true;
  const live = entry.status === 'approved' && !entry.hiddenByRecipient;
  if (audience === 'referredTo') return entry.kind === 'referral' && live;
  if (audience === 'public') return entry.kind === 'recommendation' && live;
  return false;
};

export const hasServerHeldEndorsements = user => {
  const received = metadataOf(user).endorsementsReceived;
  return !!received && typeof received === 'object';
};

const newestFirst = entries =>
  [...entries].sort((a, b) => String(b?.requestedAt || '').localeCompare(String(a?.requestedAt || '')));

const valuesOf = map => (map && typeof map === 'object' ? Object.values(map).filter(Boolean) : []);

// The old lists recorded approval as true (approved), false (declined) or absent (still asked).
const legacyStatus = approved => (approved === true ? 'approved' : approved === false ? 'declined' : 'requested');

const legacyParty = party => ({
  id: party?.id || null,
  name: party?.label || party?.name || '',
  listingId: party?.listingId || null,
});

const selfParty = user => ({
  id: user?.id?.uuid || null,
  name: profileOf(user).displayName || '',
  listingId: null,
});

const legacyEntry = ({ kind, source, index, item, giver, recipient, referredTo }) => ({
  id: `legacy-${source}-${index}`,
  legacy: true,
  kind,
  giver,
  recipient,
  referredTo: referredTo || null,
  requestNote: item?.request_description || '',
  wording: item?.description || null,
  status: legacyStatus(kind === 'referral' ? item?.referral_approved : item?.recommendation_approved),
  requestCount: 1,
  hiddenByRecipient: false,
  requestedAt: null,
  decidedAt: null,
  withdrawnAt: null,
  hiddenAt: null,
});

const listOf = value => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object') : []);

const legacyReceived = (user, publicData = publicDataOf(user)) => {
  const me = selfParty(user);
  return [
    ...listOf(publicData.referrals).map((item, index) =>
      legacyEntry({
        kind: 'referral',
        source: 'referrals',
        index,
        item,
        giver: legacyParty(item.referral_from),
        recipient: me,
        referredTo: item.referral_for ? legacyParty(item.referral_for) : null,
      })
    ),
    ...listOf(publicData.recommendations).map((item, index) =>
      legacyEntry({
        kind: 'recommendation',
        source: 'recommendations',
        index,
        item,
        giver: legacyParty(item.recommendation_from),
        recipient: me,
      })
    ),
  ];
};

const legacyGiven = (user, publicData = publicDataOf(user)) => {
  const me = selfParty(user);
  return [
    ...listOf(publicData.referralsGiven).map((item, index) =>
      legacyEntry({
        kind: 'referral',
        source: 'referralsGiven',
        index,
        item,
        giver: me,
        recipient: legacyParty(item.referral_to),
        referredTo: item.referral_for ? legacyParty(item.referral_for) : null,
      })
    ),
    ...listOf(publicData.recommendationsGiven).map((item, index) =>
      legacyEntry({
        kind: 'recommendation',
        source: 'recommendationsGiven',
        index,
        item,
        giver: me,
        recipient: legacyParty(item.recommendation_to),
      })
    ),
  ];
};

// What this person asked for, or was given.
export const receivedEndorsementsOf = user => {
  if (!user) return [];
  return hasServerHeldEndorsements(user)
    ? newestFirst(valuesOf(metadataOf(user).endorsementsReceived))
    : legacyReceived(user);
};

// What this person gave, or was asked to give.
export const givenEndorsementsOf = user => {
  if (!user) return [];
  return hasServerHeldEndorsements(user)
    ? newestFirst(valuesOf(metadataOf(user).endorsementsGiven))
    : legacyGiven(user);
};

// Approved referrals of someone else to this person.
export const referralsNamingMeOf = user => {
  if (!user || !hasServerHeldEndorsements(user)) return [];
  return newestFirst(valuesOf(metadataOf(user).referralsNamingMe).filter(e => visibleTo(e, 'referredTo')));
};

// The old public profile read its lists from the profile listing, which the settings page kept in
// step with the author's profile; the author's own lists are used when the listing has none.
const legacyPublicDataFor = (author, listing, key) => {
  const listingPublicData = listing?.attributes?.publicData || {};
  return Array.isArray(listingPublicData[key]) ? listingPublicData : publicDataOf(author);
};

const isPublicRecommendation = e => e.kind === 'recommendation' && visibleTo(e, 'public');

// Recommendations the author was given, as anyone viewing their public profile sees them.
export const publicRecommendationsOf = (author, listing) => {
  if (!author) return [];
  const entries = hasServerHeldEndorsements(author)
    ? receivedEndorsementsOf(author)
    : legacyReceived(author, legacyPublicDataFor(author, listing, 'recommendations'));
  return entries.filter(isPublicRecommendation);
};

// Recommendations the author gave, as anyone viewing their public profile sees them.
export const publicRecommendationsGivenOf = (author, listing) => {
  if (!author) return [];
  const entries = hasServerHeldEndorsements(author)
    ? givenEndorsementsOf(author)
    : legacyGiven(author, legacyPublicDataFor(author, listing, 'recommendationsGiven'));
  return entries.filter(isPublicRecommendation);
};
